import { useEffect, useState } from "react";
import { AlertCircle, Download, FileText, RefreshCw } from "lucide-react";
import { Badge, Button, Card } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { portal, pdfHref, type PortalEstimate } from "../../../lib/portal";
import { fmtDate, fmtAmount, genericStatusVariant } from "../../../lib/format";
import { EmptyState, ListSkeleton, Notice, PageHead, RowButton } from "../../ui";
import { shownStatus } from "./lib";

/** The columns of a row from `md` up; the header row shares them. */
const COLS = "md:grid md:grid-cols-[minmax(0,1fr)_9rem_8rem_10rem] md:items-center md:gap-4";

/**
 * Every quote this client may read, the ones still waiting for an answer
 * marked as such.
 *
 * `answerable` comes from the server and is never re-derived here - see
 * `lib.ts`. The row itself is the way in; the answer is given on the quote's
 * own page, where the lines being agreed to are in front of the reader.
 */
export function EstimateList({ onOpen }: { onOpen: (id: string) => void }) {
  const t = useT();
  const { locale } = useI18n();
  const [estimates, setEstimates] = useState<PortalEstimate[] | null>(null);
  const [failed, setFailed] = useState(false);

  function load() {
    setFailed(false);
    setEstimates(null);
    void portal.estimates().then(setEstimates).catch(() => setFailed(true));
  }
  useEffect(load, []);

  const waiting = estimates?.filter((e) => e.answerable).length ?? 0;

  return (
    <div>
      <PageHead title={t("portal.estimates.title")} description={t("portal.estimates.lead")} />

      {waiting > 0 && (
        <Notice icon={FileText} className="mb-4">
          {/* One has its own sentence: this app's `t()` has no plural step, and
              "1" in the counted sentence takes the wrong case in Estonian. */}
          {waiting === 1 ? t("portal.estimates.pendingBannerOne") : t("portal.estimates.pendingBanner", { count: waiting })}
        </Notice>
      )}

      <Card className="overflow-hidden">
        {failed ? (
          <EmptyState
            icon={AlertCircle}
            title={t("portal.loadFailed")}
            action={
              <Button variant="outline" onClick={load}>
                <RefreshCw />
                {t("portal.retry")}
              </Button>
            }
          />
        ) : !estimates ? (
          <ListSkeleton rows={3} label={t("common.loading")} />
        ) : estimates.length === 0 ? (
          <EmptyState icon={FileText} title={t("portal.estimates.emptyTitle")} body={t("portal.estimates.emptyBody")} />
        ) : (
          <>
            <div
              aria-hidden="true"
              className={`hidden border-b border-border bg-muted/40 py-2.5 pl-5 pr-[4.5rem] text-xs font-semibold uppercase tracking-wider text-foreground/70 ${COLS}`}
            >
              <span>{t("portal.estimates.one")}</span>
              <span>{t("portal.estimates.validUntil")}</span>
              <span className="text-right">{t("portal.invoices.total")}</span>
              <span>{t("portal.status")}</span>
            </div>
            <ul className="divide-y divide-border">
              {estimates.map((est) => {
                const total = fmtAmount(est.totalCents, est.currency, locale);
                const valid = fmtDate(est.validUntil, locale);
                const status = est.answerable ? (
                  <Badge>{t("portal.estimates.needsAnswer")}</Badge>
                ) : (
                  <Badge variant={genericStatusVariant(shownStatus(est))}>
                    {t(`portal.estimateStatus.${shownStatus(est)}`)}
                  </Badge>
                );
                return (
                  <li key={est.id} className="flex items-stretch">
                    <RowButton onClick={() => onOpen(est.id)} className="min-w-0 flex-1 pr-2">
                      <span className={`hidden w-full ${COLS}`}>
                        <span className="min-w-0">
                          <span className="block font-semibold text-primary">{est.number}</span>
                          {est.title && <span className="block truncate text-foreground/70">{est.title}</span>}
                        </span>
                        <span className="tabular-nums text-foreground/70">{valid}</span>
                        <span className="text-right font-semibold tabular-nums">{total}</span>
                        <span>{status}</span>
                      </span>
                      <span className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1.5 md:hidden">
                        <span className="min-w-0">
                          <span className="block font-semibold">{est.number}</span>
                          {est.title && <span className="block truncate text-foreground/70">{est.title}</span>}
                        </span>
                        <span className="text-right font-semibold tabular-nums">{total}</span>
                        <span className="text-xs text-foreground/70">
                          {t("portal.estimates.validUntil")} {valid}
                        </span>
                        <span className="justify-self-end">{status}</span>
                      </span>
                    </RowButton>
                    <Button variant="ghost" size="icon" asChild className="my-auto mr-3 shrink-0">
                      <a
                        href={pdfHref(`/portal/estimates/${est.id}/pdf`)}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={t("portal.pdfOf", { number: est.number })}
                        title={t("portal.invoices.downloadPdf")}
                      >
                        <Download />
                      </a>
                    </Button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </Card>
    </div>
  );
}
